import React, { useContext, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/shadcn/card";
import { UserContext } from "@/store/context/UserContext.tsx";
import { useTranslation } from "react-i18next";
import { constants } from "@/constants";
import { TopUpModalTochka } from "@/components/billing/TopUpModalTochka.tsx";
import { useQuery } from "react-query";
import { queryKey } from "@/store/queryKeys.ts";
import { PaymentList } from "@/components/billing/PaymentList.tsx";
import { ApplyPromocodeModal } from "@/components/billing/ApplyPromocodeModal.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/shadcn/dialog.tsx";
import { ExternalLink } from "@/components/ExternalLink.tsx";
import { Button } from "@/components/shadcn/button.tsx";
import { useNavigate } from "react-router-dom";
import {Icon} from "@/components/Icon.tsx";

export const BalanceCashbackAlert = ({ balanceData }: { balanceData: any }) => {
  if (!balanceData || !balanceData.bonus_cashback) {
    return null;
  }

  return (
    <div className="mb-4 border rounded-lg p-2 bg-sky-50">
      Кэшбэк активирован: пополните Ваш баланс и мы{" "}
      <b>вернём {balanceData.bonus_cashback}% бонусами</b>
    </div>
  );
};

const BusinessAccountBtn = ({ className }: { className: string }) => {
  return (
    <a
      href="https://forms.yandex.ru/u/686cf38290fa7bd542ab5c5f"
      target="_blank"
      className={className}
    >
      <Button variant="outline" className="[&_svg]:size-6 w-full">
        <Icon icon="fluent:briefcase-24-regular" />
        Создать бизнес-аккаунт
      </Button>
    </a>
  );
};

export const ProfilePageMostAI: React.FC = () => {
  const { userSettings, invalidateSettings } = useContext(UserContext);
  const { t } = useTranslation();
  const navigate = useNavigate();

  let balanceQuery = useQuery(queryKey.balance, async function () {
    let res = await fetch(`${constants.url.API_PREFIX}billing/balance`);
    return res.json();
  });

  const [promocodeModalOpen, setPromocodeModalOpen] = React.useState(false);

  return (
    <div className="grid grid-cols-1 gap-3">
      <BusinessAccountBtn className="md:hidden" />

      <Card className="">
        <CardHeader className="relative flex lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex gap-3 items-center">
            {userSettings.avatar_url && (
              <img
                src={userSettings.avatar_url}
                alt="avatar"
                className="w-14 h-14 rounded-full inline-block mr-1"
              />
            )}
            <div>
              <CardTitle>
                {userSettings.name ? userSettings.name : userSettings.email}
              </CardTitle>
              <CardDescription className="pt-1">
                {userSettings.name && <p>{userSettings.email}</p>}
                ID:&nbsp;{userSettings.userID}
              </CardDescription>
            </div>
          </div>
          <div>
            <BusinessAccountBtn className="mr-2 hidden md:inline-flex" />
            <Button
              className="[&_svg]:size-6 mt-2"
              onClick={() => {
                navigate("/profile/invite");
              }}
            >
              <Icon icon="iconoir:coin" />
              Партнерская программа
            </Button>
          </div>
        </CardHeader>

        {/*<CardFooter className="flex justify-between"></CardFooter>*/}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="">
          <CardHeader className="relative">
            <CardTitle>{t("balance")}</CardTitle>
            <CardDescription></CardDescription>
          </CardHeader>
          <CardContent>
            <BalanceCashbackAlert balanceData={balanceQuery.data} />
            <div className="flex flex-row items-center gap-4">
              {balanceQuery.data && (
                <div className="text-2xl">
                  {balanceQuery.data.balance.toFixed(2)}&nbsp;₽
                </div>
              )}
              <TopUpModalTochka
                onPromocodeClick={() => {
                  setPromocodeModalOpen(true);
                }}
              />
            </div>
          </CardContent>
          <CardFooter className="flex justify-between"></CardFooter>
        </Card>

        <Card className="">
          <CardHeader className="relative">
            <Dialog>
              <DialogTrigger asChild>
                <CardTitle className="flex items-center cursor-pointer">
                  {t("bonuses")}
                  <Icon
                    icon="fluent:question-circle-24-regular"
                    width={22}
                    className="ml-2"
                  />
                </CardTitle>
              </DialogTrigger>
              <DialogContent className="">
                <DialogHeader>
                  <DialogTitle>Бонусная программа</DialogTitle>
                  <DialogDescription></DialogDescription>
                </DialogHeader>

                <p>
                  Если у вас есть промокод или подарочная карта - вы можете
                  увеличить бонусный баланс.
                </p>
                <p>
                  При наличии бонусов на балансе - они списываются в первую
                  очередь, затем, основной баланс
                </p>

                <p className="font-bold">1 ₽ = 2 бонуса</p>

                <div className="flex items-center gap-4 mt-4 border rounded-lg p-2 bg-sky-50">
                  <img
                    src={`${constants.baseURL}/logo/integrations/telegram.svg`}
                    className="w-10 rounded-lg"
                  />
                  <div>
                    Подписывайтесь на{" "}
                    <ExternalLink href="https://t.me/mostAI_com">
                      наш канал в телеграм
                    </ExternalLink>
                    где мы разыгрываем бонусы среди наших подписчиков
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <CardDescription></CardDescription>
          </CardHeader>
          <CardContent className="flex flex-row items-center gap-4">
            {balanceQuery.data && (
              <div className="text-2xl flex items-center gap-1">
                {balanceQuery.data.bonuses.toFixed(2)}
                <Icon icon="pepicons-pencil:coins" width={24} />
              </div>
            )}

            <ApplyPromocodeModal
              open={promocodeModalOpen}
              onOpenChange={setPromocodeModalOpen}
            />
          </CardContent>
          <CardFooter className="flex justify-between"></CardFooter>
        </Card>
      </div>

      <PaymentList />
    </div>
  );
};
